import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { sendNotification } from "../utils/sendNotification";

function isDueOverdue(m) {
  const due = m.due_date || m.date_prevue;
  if (!due) return false;
  const d = new Date(due);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  if (d >= start) return false;
  if (m.status === "done" || m.status === "termine") return false;
  return true;
}

const DEFAULT_MILESTONES = [
  "Étude",
  "Permis",
  "Démarrage chantier",
  "Gros œuvre",
  "Second œuvre",
  "Livraison",
];

export default function MilestonesPage({ isOPCMandate = false, cabinetId = null }) {
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  useEffect(() => {
    if (cabinetId == null) return;
    chargerProjects();
    chargerMilestones();
  }, [cabinetId]);

  async function chargerProjects() {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("cabinet_id", cabinetId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Erreur chargement projets:", error);
    } else {
      setProjects(data || []);
    }
  }

  async function chargerMilestones() {
    const { data, error } = await supabase
      .from("milestones")
      .select("*")
      .eq("cabinet_id", cabinetId)
      .order("number", { ascending: true });

    if (error) {
      console.error("Erreur chargement jalons:", error);
    } else {
      setMilestones(data || []);
    }
  }

  async function initialiserJalonsProjet(projectId) {
    if (!projectId) return;

    const existing = milestones.filter(
      (m) => String(m.project_id) === String(projectId)
    );

    if (existing.length > 0) {
      alert("Les jalons de ce projet existent déjà.");
      return;
    }

    const project = projects.find((p) => String(p.id) === String(projectId));
    if (!project) return;

    const nouveauxJalons = DEFAULT_MILESTONES.map((titre, index) => ({
      project_id: project.id,
      cabinet_id: cabinetId,
      number: index + 1,
      title: titre,
      status: "pending",
      notes: "",
    }));

    const { error } = await supabase.from("milestones").insert(nouveauxJalons);

    if (error) {
      console.error("Erreur initialisation jalons:", error);
      alert("Erreur lors de l'initialisation des jalons.");
      return;
    }

    chargerMilestones();
  }

  async function updateMilestone(id, field, value) {
    const payload = { [field]: value };

    if (field === "status" && (value === "done" || value === "termine")) {
      payload.validated_at = new Date().toISOString();
      payload.date_validation = new Date().toISOString();
    }

    const milestone = milestones.find((m) => m.id === id);
    const projectId = milestone?.project_id;

    const { error } = await supabase
      .from("milestones")
      .update(payload)
      .eq("id", id)
      .eq("cabinet_id", cabinetId);

    if (error) {
      console.error("Erreur mise à jour jalon:", error);
      alert("Erreur lors de la mise à jour.");
      return;
    }

    const merged = { ...milestone, ...payload };
    const { data: authData } = await supabase.auth.getUser();
    const currentUser = authData?.user;
    if (currentUser?.id) {
      const project = projects.find((p) => String(p.id) === String(milestone.project_id));
      const nom_jalon = milestone.title || "Sans titre";
      const nom_projet = project?.name || "—";
      const montant = Number(milestone.amount ?? milestone.montant ?? milestone.expected_amount ?? 0);
      try {
        if (field === "status" && (value === "done" || value === "termine")) {
          await sendNotification({
            supabase,
            destinataireId: currentUser.id,
            expediteurId: currentUser.id,
            type: "jalon",
            titre: "Jalon validé",
            message: `Le jalon ${nom_jalon} est confirmé.`,
            projetId: milestone.project_id,
            sendEmail: false,
          });
        } else {
          const statutImpaye = field === "status" && value === "blocked";
          const echeanceDepassee =
            (field === "due_date" || field === "date_prevue") && isDueOverdue(merged);
          const statutChangeEtEcheance =
            field === "status" && isDueOverdue(merged) && value !== "done" && value !== "termine";
          if (statutImpaye || echeanceDepassee || statutChangeEtEcheance) {
            await sendNotification({
              supabase,
              destinataireId: currentUser.id,
              expediteurId: currentUser.id,
              type: "jalon",
              titre: "Jalon en attente de paiement",
              message: isOPCMandate
                ? `Le jalon ${nom_jalon} du projet ${nom_projet} est en attente de paiement.`
                : `Le jalon ${nom_jalon} du projet ${nom_projet} est en attente de paiement. Montant : ${Number(montant).toLocaleString("fr-FR")} MAD`,
              projetId: milestone.project_id,
              sendEmail: false,
            });
          }
        }
      } catch (e) {
        console.error("sendNotification:", e);
      }
    }

    if (field === "status" && projectId) {
      const { data: projectMilestones } = await supabase
        .from("milestones")
        .select("id, status")
        .eq("project_id", projectId)
        .eq("cabinet_id", cabinetId)
        .order("number", { ascending: true });
      const total = projectMilestones?.length ?? 0;
      const termines = projectMilestones?.filter((m) => m.status === "termine" || m.status === "done").length ?? 0;
      const progress = total ? Math.round((termines / total) * 100) : 0;
      await supabase
        .from("projects")
        .update({ progress })
        .eq("id", projectId)
        .eq("cabinet_id", cabinetId);
    }

    chargerMilestones();
    chargerProjects();
  }

  async function supprimerJalonsProjet(projectId) {
    if (!projectId) return;

    const { error } = await supabase
      .from("milestones")
      .delete()
      .eq("project_id", projectId)
      .eq("cabinet_id", cabinetId);

    if (error) {
      console.error("Erreur suppression jalons:", error);
      alert("Erreur lors de la suppression.");
      return;
    }

    chargerMilestones();
  }

  function getProjectMilestones(projectId) {
    return milestones
      .filter((m) => String(m.project_id) === String(projectId))
      .sort((a, b) => a.number - b.number);
  }

  function getProgress(projectId) {
    const list = getProjectMilestones(projectId);
    if (list.length === 0) return 0;
    const termines = list.filter((m) => m.status === "termine" || m.status === "done").length;
    return Math.round((termines / list.length) * 100);
  }

  function getStatutStyle(statut) {
    if (statut === "done" || statut === "termine") return { color: "#1E8449", fontWeight: 700 };
    if (statut === "in_progress" || statut === "en_cours") return { color: "#D68910", fontWeight: 700 };
    if (statut === "blocked") return { color: "#C0392B", fontWeight: 700 };
    return { color: "#555", fontWeight: 700 };
  }

  function getDisplayStatus(statut) {
    if (statut === "done" || statut === "termine") return "Conforme";
    if (statut === "in_progress" || statut === "en_cours") return "Partiel";
    if (statut === "blocked") return "Impayé";
    return "En attente";
  }

  function getStatusBadgeStyle(displayStatus) {
    if (displayStatus === "Conforme") {
      return {
        color: "#5C8A5C",
        background: "rgba(92,138,92,0.14)",
        border: "1px solid rgba(92,138,92,0.4)",
      };
    }
    if (displayStatus === "Partiel") {
      return {
        color: "#C09A5B",
        background: "rgba(192,154,91,0.14)",
        border: "1px solid rgba(192,154,91,0.4)",
      };
    }
    if (displayStatus === "Impayé") {
      return {
        color: "#B85C3A",
        background: "rgba(184,92,58,0.14)",
        border: "1px solid rgba(184,92,58,0.4)",
      };
    }
    return {
      color: "#8B7361",
      background: "rgba(139,115,97,0.14)",
      border: "1px solid rgba(139,115,97,0.4)",
    };
  }

  function getMilestoneProgress(statut) {
    if (statut === "done" || statut === "termine") return 100;
    if (statut === "in_progress" || statut === "en_cours") return 55;
    if (statut === "blocked") return 15;
    return 0;
  }

  const montantInputStyle = (base = {}) => ({
    ...base,
    opacity: isOPCMandate ? 0.3 : 1,
  });

  const selectedProject = projects.find(
    (p) => String(p.id) === String(selectedProjectId)
  );

  const selectedProjectMilestones = selectedProject
    ? getProjectMilestones(selectedProject.id)
    : [];

  const progress = selectedProject ? getProgress(selectedProject.id) : 0;
  const kpiTotal = selectedProjectMilestones.length;
  const kpiConformes = selectedProjectMilestones.filter((m) => getDisplayStatus(m.status) === "Conforme").length;
  const kpiPartiels = selectedProjectMilestones.filter((m) => getDisplayStatus(m.status) === "Partiel").length;
  const kpiImpayes = selectedProjectMilestones.filter((m) => getDisplayStatus(m.status) === "Impayé").length;

  if (cabinetId == null) {
    return (
      <main style={{ padding: 24, minHeight: "100vh", background: "#F8F4EE" }}>
        <div style={{ textAlign: "center", color: "#8B7361", fontFamily: "'DM Sans', sans-serif" }}>
          Chargement...
        </div>
      </main>
    );
  }

  return (
    <main style={{ padding: 24, minHeight: "100vh", background: "#F8F4EE" }}><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;700&display=swap');
      `}</style><header style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 18, marginBottom: 16 }}><div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}><div><h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#3D2E20" }}>Jalons</h1><p style={{ marginTop: 6, marginBottom: 0, fontFamily: "'DM Sans', sans-serif", color: "#8B7361", fontSize: 13 }}>
              Suivi OPC YOMA par projet
            </p></div><div style={{ minWidth: 320, width: 380 }}><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Projet
            </label><select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", background: "#FFF", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#3D2E20" }}
            ><option value="">Sélectionner un projet</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} — {project.client_name}
                </option>
              ))}
            </select></div></div></header>

      {selectedProject && (
        <><div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 16 }}><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Total jalons</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#3D2E20", fontWeight: 700 }}>{kpiTotal}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(92,138,92,0.4)", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Jalons conformes</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#5C8A5C", fontWeight: 700 }}>{kpiConformes}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(192,154,91,0.4)", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Jalons en attente</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#C09A5B", fontWeight: 700 }}>{kpiPartiels}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(184,92,58,0.4)", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Jalons impayés</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#B85C3A", fontWeight: 700 }}>{kpiImpayes}</div></div></div><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 14, marginBottom: 14 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8B7361" }}>
                Projet : <strong style={{ color: "#3D2E20" }}>{selectedProject.name}</strong> — {selectedProject.client_name || "Client non renseigné"}
              </div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8B7361" }}>Avancement global : <strong style={{ color: "#3D2E20" }}>{progress}%</strong></div></div><div style={{ width: "100%", height: 10, background: "#EFE4D4", borderRadius: 999 }}><div style={{ width: `${progress}%`, height: "100%", borderRadius: 999, background: "#C09A5B" }} /></div></div><div style={{ display: "flex", gap: 10, marginBottom: 14 }}><button
              onClick={() => initialiserJalonsProjet(selectedProjectId)}
              style={{ padding: "9px 14px", borderRadius: 8, background: "#FFFFFF", border: "1px solid #D8C9B8", color: "#8B7361", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >
              Initialiser les 6 jalons
            </button><button
              onClick={() => supprimerJalonsProjet(selectedProjectId)}
              style={{ padding: "9px 14px", borderRadius: 8, background: "rgba(184,92,58,0.08)", border: "1px solid rgba(184,92,58,0.35)", color: "#B85C3A", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >
              Supprimer les jalons
            </button></div>
            <div style={{ overflowX: "auto", background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #E3D7C8", background: "#FDFBF8" }}>
                    <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, color: "#8B7361", textTransform: "uppercase", letterSpacing: "0.08em" }}>Jalon</th>
                    <th
                      style={{
                        display: isOPCMandate ? "none" : "",
                        textAlign: "right",
                        padding: "12px 14px",
                        fontSize: 11,
                        color: "#8B7361",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Montant (MAD)
                    </th>
                    <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, color: "#8B7361", textTransform: "uppercase", letterSpacing: "0.08em" }}>Date prévue</th>
                    <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, color: "#8B7361", textTransform: "uppercase", letterSpacing: "0.08em" }}>% avancement</th>
                    <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, color: "#8B7361", textTransform: "uppercase", letterSpacing: "0.08em" }}>Statut</th>
                    <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, color: "#8B7361", textTransform: "uppercase", letterSpacing: "0.08em" }}>Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProjectMilestones.map((milestone) => {
                    const displayStatus = getDisplayStatus(milestone.status);
                    const progressMilestone = getMilestoneProgress(milestone.status);
                    const milestoneDate = milestone.due_date || milestone.date_prevue || milestone.date_validation;
                    const amount = milestone.amount ?? milestone.montant ?? milestone.expected_amount;
                    const amountStr = amount === undefined || amount === null || amount === "" ? "" : String(amount);
                    const valeur =
                      amount === undefined || amount === null || amount === ""
                        ? null
                        : (() => {
                            const n = Number(amount);
                            return Number.isFinite(n) ? n.toLocaleString("fr-FR") : String(amount);
                          })();
                    return (
                      <tr key={milestone.id} style={{ borderBottom: "1px solid #EFE4D4" }}>
                        <td style={{ padding: "14px", verticalAlign: "top" }}>
                          <div style={{ fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
                            V{milestone.number} — {milestone.title || "Sans titre"}
                          </div>
                        </td>
                        <td
                          style={{
                            display: isOPCMandate ? "none" : "",
                            padding: "14px",
                            verticalAlign: "top",
                            textAlign: "right",
                          }}
                        >
                          {!isOPCMandate && (
                            <div
                              style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "#C09A5B",
                                fontSize: 18,
                                fontWeight: 700,
                                marginBottom: 8,
                              }}
                            >
                              {valeur === null ? "—" : `${valeur} MAD`}
                            </div>
                          )}
                          <label style={{ display: "block", fontSize: 10, color: "#8B7361", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Saisie MAD</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            disabled={isOPCMandate}
                            value={amountStr}
                            onChange={(e) => updateMilestone(milestone.id, "amount", e.target.value === "" ? null : e.target.value)}
                            placeholder="0"
                            style={montantInputStyle({
                              width: "100%",
                              maxWidth: 140,
                              border: "1px solid #D8C9B8",
                              borderRadius: 8,
                              padding: "8px 10px",
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: 13,
                              color: "#3D2E20",
                              textAlign: "right",
                            })}
                          />
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "top", color: "#8B7361", fontSize: 12 }}>
                          {milestoneDate ? new Date(milestoneDate).toLocaleDateString("fr-FR") : "Non définie"}
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "top", minWidth: 120 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12, color: "#8B7361" }}>
                            <span />
                            <strong style={{ color: "#3D2E20" }}>{progressMilestone}%</strong>
                          </div>
                          <div style={{ width: "100%", height: 8, background: "#EFE4D4", borderRadius: 999 }}>
                            <div style={{ width: `${progressMilestone}%`, height: "100%", borderRadius: 999, background: "#C09A5B" }} />
                          </div>
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "top", minWidth: 140 }}>
                          <span style={{ display: "inline-block", marginBottom: 8, borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", ...getStatusBadgeStyle(displayStatus) }}>
                            {displayStatus}
                          </span>
                          <select
                            value={milestone.status}
                            onChange={(e) => updateMilestone(milestone.id, "status", e.target.value)}
                            style={{
                              width: "100%",
                              border: "1px solid #D8C9B8",
                              borderRadius: 8,
                              padding: "8px 10px",
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: 12,
                              background: "#FFF",
                              ...getStatutStyle(milestone.status),
                            }}
                          >
                            <option value="pending">À faire</option>
                            <option value="in_progress">En cours</option>
                            <option value="done">Terminé</option>
                            <option value="blocked">Bloqué</option>
                          </select>
                        </td>
                        <td style={{ padding: "14px", verticalAlign: "top", minWidth: 180 }}>
                          <input
                            type="text"
                            value={milestone.notes || ""}
                            onChange={(e) => updateMilestone(milestone.id, "notes", e.target.value)}
                            placeholder="Commentaire"
                            style={{
                              width: "100%",
                              border: "1px solid #D8C9B8",
                              borderRadius: 8,
                              padding: "9px 10px",
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: 13,
                              color: "#3D2E20",
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div></>
      )}
      {!selectedProject && (
        <section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: "40px 24px", textAlign: "center" }}><div style={{ fontSize: 34, marginBottom: 10 }}>◈</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#3D2E20", marginBottom: 6 }}>
            Aucun projet sélectionné
          </div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#8B7361", marginBottom: 16 }}>
            Sélectionnez un projet dans l'en-tête pour afficher la timeline OPC.
          </div><button
            onClick={() => {
              if (projects[0]?.id) setSelectedProjectId(String(projects[0].id));
            }}
            style={{ padding: "10px 16px", border: "1px solid #D8C9B8", borderRadius: 8, background: "#FFFFFF", color: "#8B7361", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            Sélectionner un projet
          </button></section>
      )}
    </main>
  );
}